// The staff dashboard (/admin): ID checks and the email log. Every ID check call is the same database
// function the app's Check IDs screens use, and the database refuses anyone who isn't staff.

export const identityBucket = 'identity-checks';

export const routeLabels = { photo_id: 'Photo ID', documents: 'Documents' };

export const idTypeLabels = {
  passport: 'Passport',
  driving_licence: 'UK driving licence',
  residence_permit: 'Residence permit or card',
  pass_card: 'PASS card',
  documents: 'Documents (no photo ID)',
};

export const rejectionReasons = [
  ['blurry', 'Photos are blurry'],
  ['expired', 'ID has expired'],
  ['face_mismatch', "Selfie doesn't match the ID"],
  ['action_missing', 'Selfie action missing'],
  ['id_already_used', 'ID already used'],
  ['documents_not_accepted', 'Documents not accepted'],
  ['other', 'Other'],
];
export const rejectionReasonLabels = Object.fromEntries(rejectionReasons);

const frontLabels = {
  passport: 'Passport photo page',
  driving_licence: 'Front of driving licence',
  residence_permit: 'Front of residence permit',
  pass_card: 'Front of PASS card',
  documents: 'Front of ID',
};

export function slotLabel(idType, slot) {
  switch (slot) {
    case 'id_front': return frontLabels[idType] || 'Front of ID';
    case 'id_back': return 'Back of driving licence';
    case 'document_1': return 'Birth or adoption certificate';
    case 'document_2': return 'DWP or National Insurance letter';
    default: return 'Selfie';
  }
}

/** "5 hours", "2 days": how long a check has been waiting. */
export function waitingFor(submittedAt, now = Date.now()) {
  const hours = Math.floor((now - Date.parse(submittedAt)) / 3600000);
  if (!Number.isFinite(hours) || hours < 1) return 'under an hour';
  if (hours < 24) return hours === 1 ? '1 hour' : `${hours} hours`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day' : `${days} days`;
}

/** A time as the database or Resend writes it ("2026-10-14 13:05:00", "2026-10-20 17:19:05.905000+00", ISO), as a Date. No zone means UTC. */
export function parseWhen(value) {
  let text = String(value ?? '').trim().replace(' ', 'T');
  if (/T[\d:.]+[+-]\d{2}$/.test(text)) text += ':00';
  else if (/T[\d:.]+[+-]\d{4}$/.test(text)) text = `${text.slice(0, -2)}:${text.slice(-2)}`;
  else if (/T[\d:.]+$/.test(text)) text += 'Z';
  return new Date(text);
}

/** "20 Oct, 18:19" in London time; '' for nothing and '–' for something that isn't a time. */
export function formatWhen(value) {
  if (!value) return '';
  const date = parseWhen(value);
  if (Number.isNaN(date.getTime())) return '–';
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
}

export function formatDate(value) {
  if (!value) return '';
  return new Date(`${value}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/London' });
}

async function call(client, name, args) {
  const { data, error } = await client.rpc(name, args);
  if (error) throw new Error(error.message || 'Could not load. Please try again.');
  return data;
}

export const listWaitingChecks = async (client) => (await call(client, 'staff_list_identity_checks')) || [];
export const listRecentChecks = async (client) => (await call(client, 'staff_recent_identity_checks', { p_limit: 20 })) || [];
export const getCheck = (client, id) => call(client, 'staff_get_identity_check', { p_id: id });

/** Five-minute links to the check's files, keyed by slot. Files that can't be opened are left out. */
export async function fileUrls(client, files) {
  const entries = await Promise.all((files || []).map(async (file) => {
    const { data, error } = await client.storage.from(identityBucket).createSignedUrl(file.storage_path, 300);
    return [file.slot, !error && data?.signedUrl ? data.signedUrl : null];
  }));
  return Object.fromEntries(entries.filter(([, url]) => url));
}

export function validateApproval({ idNumber, nameOnId, dobOnId }, today = new Date().toISOString().slice(0, 10)) {
  if (!String(idNumber || '').trim() || !String(nameOnId || '').trim()) return 'Enter the ID number and the name on the ID.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dobOnId || ''))) return 'Enter the date of birth on the ID.';
  if (dobOnId > today) return 'The date of birth on the ID is in the future.';
  return null;
}

export async function approveCheck(client, { id, idNumber, nameOnId, dobOnId }) {
  await call(client, 'staff_approve_identity_check', {
    p_id: id, p_id_number: String(idNumber).trim(), p_name_on_id: String(nameOnId).trim(), p_dob_on_id: dobOnId,
  });
}

export async function rejectCheck(client, { id, reason, note }) {
  await call(client, 'staff_reject_identity_check', { p_id: id, p_reason: reason, p_note: String(note || '').trim() || null });
}

export function approveErrorMessage(error) {
  const message = String(error?.message || '');
  if (message.includes('IDENTITY_ALREADY_USED')) return 'This ID is already on another account. Reject this check with "ID already used".';
  if (message.includes('IDENTITY_CHECK_NOT_FOUND')) return 'This check has already been decided, or it was cancelled.';
  if (message.includes('NOT_STAFF')) return "You can't decide this check. Staff can't approve their own ID.";
  return 'Could not save. Please try again.';
}

// Emails

export const mailboxes = ['team', 'ryan', 'hello', 'naima'];
const ourDomain = '@residentservice360.bedmaps.com';

const statuses = {
  delivered: ['Delivered', 'good'],
  opened: ['Opened', 'good'],
  clicked: ['Clicked', 'good'],
  sent: ['Sent', 'neutral'],
  queued: ['Waiting to send', 'neutral'],
  scheduled: ['Scheduled', 'neutral'],
  delivery_delayed: ['Delayed', 'warn'],
  bounced: ['Bounced', 'bad'],
  complained: ['Marked as spam', 'bad'],
  failed: ['Failed', 'bad'],
  canceled: ['Cancelled', 'neutral'],
};

export function emailStatus(value) {
  const [label, tone] = statuses[value] || [String(value || 'Unknown'), 'neutral'];
  return { label, tone };
}

/** Which of our addresses an email was sent from or to ("team", "ryan"…), or null. */
export function mailboxOf(email) {
  const addresses = [email.from, ...(email.to || [])].map((value) => String(value || '').toLowerCase());
  const ours = addresses.find((address) => address.endsWith(ourDomain));
  return ours ? ours.slice(0, -ourDomain.length) : null;
}

export function filterEmails(rows, { mailbox = '', status = '', query = '' } = {}) {
  const words = query.trim().toLowerCase();
  return (rows || []).filter((email) => (!mailbox || mailboxOf(email) === mailbox)
    && (!status || email.status === status)
    && (!words || [email.subject, email.from, ...(email.to || [])].some((value) => String(value || '').toLowerCase().includes(words))));
}

export async function fetchEmailLog(client, fetchImpl = globalThis.fetch) {
  const { data } = await client.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('Sign in again to see the emails.');
  const response = await fetchImpl('/.netlify/functions/admin-emails', { headers: { authorization: `Bearer ${token}` } });
  const body = await response.json().catch(() => ({}));
  if (response.status === 403) throw new Error('The email log is only for Resident Service 360 staff.');
  if (!response.ok) throw new Error('Could not load the emails from Resend. Please try again.');
  return body;
}

/** One whole email (kind is "sent" or "received"), for the dropdown in the Emails tab. */
export async function fetchEmail(client, { id, kind }, fetchImpl = globalThis.fetch) {
  const { data } = await client.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('Sign in again to see the emails.');
  const response = await fetchImpl(`/.netlify/functions/admin-emails?id=${encodeURIComponent(id)}&kind=${encodeURIComponent(kind)}`, { headers: { authorization: `Bearer ${token}` } });
  const body = await response.json().catch(() => ({}));
  if (response.status === 403) throw new Error('The email log is only for Resident Service 360 staff.');
  if (response.status === 404) throw new Error("This email isn't in Resend any more.");
  if (!response.ok) throw new Error('Could not load this email from Resend. Please try again.');
  return body;
}

/** Everything one of our addresses ("naima") sent and received, read live from Resend, for the outreach tracking. It can take a few seconds. */
/** How much of Resend's free plan is used: `{ today, month, domains, limits: { day, month, domains }, complete }` (see readUsage). */
export async function fetchEmailUsage(client, fetchImpl = globalThis.fetch) {
  const { data } = await client.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('Sign in again to see the Resend usage.');
  const response = await fetchImpl('/.netlify/functions/admin-emails?view=usage', { headers: { authorization: `Bearer ${token}` } });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error('Could not load the Resend usage. Please try again.');
  return body;
}

export async function fetchMailbox(client, mailbox, fetchImpl = globalThis.fetch) {
  const { data } = await client.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('Sign in again to see the emails.');
  const response = await fetchImpl(`/.netlify/functions/admin-emails?view=mailbox&mailbox=${encodeURIComponent(mailbox)}`, { headers: { authorization: `Bearer ${token}` } });
  const body = await response.json().catch(() => ({}));
  if (response.status === 403) throw new Error('The email log is only for Resident Service 360 staff.');
  if (!response.ok) throw new Error('Could not load the emails from Resend. Please try again.');
  return body;
}
